import React, { useEffect, useRef } from 'react'
import { ClipboardList, Map as MapIcon, VideoOff, Circle } from 'lucide-react'
import { useEinsatz, oeffneEinsatzWahl } from '@pilot-hub/ui'

/** Modul-lokaler Leerzustand: großes Symbol, ein Satz, ein Primärknopf, Statuszeile „Letzter Abgleich“. */
export interface LeerZustandProps {
  icon: React.ReactNode
  titel: string
  text: string
  /** Primärknopf (Handschuh-tauglich, ≥ 56 px) */
  aktion?: { label: string; onClick?: () => void; href?: string }
  /** Zeitpunkt des letzten Datenabgleichs (Statuszeile) */
  abgleichAm?: Date | null
  /** Zusatzinhalt, z. B. Mini-Checkliste */
  children?: React.ReactNode
}

const zeit = (d: Date) => d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })

export function LeerZustand({ icon, titel, text, aktion, abgleichAm, children }: LeerZustandProps) {
  const knopf = 'mt-5 inline-flex min-h-[56px] items-center justify-center rounded-xl bg-[rgb(125_211_252/0.18)] px-6 text-base font-semibold text-[rgb(125_211_252)] ring-1 ring-[rgb(125_211_252/0.5)] transition-colors hover:bg-[rgb(125_211_252/0.28)]'
  return (
    <div className="mx-auto flex w-full max-w-md flex-col items-center rounded-2xl border border-dark-border bg-dark-bg-secondary/60 p-6 text-center backdrop-blur" role="status">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-dark-bg-tertiary text-dark-text-muted [&_svg]:h-9 [&_svg]:w-9">{icon}</div>
      <h2 className="mt-4 text-lg font-semibold text-dark-text">{titel}</h2>
      <p className="mt-1.5 text-sm text-dark-text-muted">{text}</p>
      {children}
      {aktion && (aktion.href
        ? <a href={aktion.href} target="_blank" rel="noreferrer" className={knopf}>{aktion.label}</a>
        : <button type="button" onClick={aktion.onClick} className={knopf}>{aktion.label}</button>)}
      {abgleichAm && <p className="mt-4 text-xs tabular-nums text-dark-text-subtle">Letzter Abgleich {zeit(abgleichAm)}</p>}
    </div>
  )
}

/** Zeitpunkt, an dem sich `abhaengig` zuletzt geändert hat (Datenabgleich der Seite). */
export function useAbgleichZeit(...abhaengig: unknown[]): Date {
  const ref = useRef<Date>(new Date())
  useEffect(() => { ref.current = new Date() }, abhaengig) // eslint-disable-line react-hooks/exhaustive-deps
  return ref.current
}

/** Kein Einsatz zugeordnet: Co-Pilot-Dashboard/Checklisten bzw. Taktikkarte. */
export function KeinEinsatzLeer({ art }: { art: 'checklisten' | 'karte' }) {
  const ew = useEinsatz()
  const abgleich = useAbgleichZeit(ew.einsaetze, ew.loading)
  const checklisten = art === 'checklisten'
  return (
    <div className="p-2 sm:p-4">
      <LeerZustand
        icon={checklisten ? <ClipboardList /> : <MapIcon />}
        titel="Kein Einsatz gewählt"
        text={checklisten
          ? 'Sobald du einem laufenden Einsatz zugeteilt bist, erscheinen hier die Checklisten deiner Flüge.'
          : 'Die Taktikkarte ist einsatzgebunden. Wähle einen Einsatz, dann erscheint die Karte.'}
        aktion={{ label: 'Zur Einsatzliste', onClick: oeffneEinsatzWahl }}
        abgleichAm={ew.loading ? null : abgleich}
      />
    </div>
  )
}

/** Cockpit ohne verbundene Drohne: Hinweis mit Mini-Checkliste und Anleitung. */
export function KeineDrohneLeer({ fehler }: { fehler?: string | null }) {
  const abgleich = useAbgleichZeit(fehler)
  const punkte = ['Fernsteuerung an', 'DJI Pilot 2 offen', 'Cloud verbunden']
  return (
    <div className="p-2 sm:p-4">
      <LeerZustand
        icon={<VideoOff />}
        titel="Keine Drohne verbunden"
        text={fehler ?? 'Das Cockpit erscheint hier, sobald die Fernsteuerung über DJI Pilot 2 angemeldet ist.'}
        aktion={{ label: 'Drohne verbinden: Anleitung DJI Pilot 2', href: '/handbuch/anwenderhandbuch-pilot-kompakt.pdf' }}
        abgleichAm={abgleich}
      >
        <ul className="mt-4 w-full space-y-2 text-left text-sm text-dark-text">
          {punkte.map(p => (
            <li key={p} className="flex min-h-[44px] items-center gap-3 rounded-lg bg-dark-bg-tertiary/60 px-3">
              <Circle className="h-5 w-5 shrink-0 text-dark-text-muted" />{p}
            </li>
          ))}
        </ul>
      </LeerZustand>
    </div>
  )
}
